import { parseISO } from "date-fns";
import { z } from "zod";

const BusinessLocationSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  modifiedAt: z.date().nullable(),
  name: z.string(),
  address: z.string(),
  city: z.string(),
  postalCode: z.string(),
  state: z.string(),
  businessId: z.string(),
  deletedAt: z.date().nullable(),
  createdBy: z.string().nullable(),
});

const CreateBusinessLocationRequestSchema = BusinessLocationSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  deletedAt: true,
  createdBy: true,
}).partial();

const UpdateBusinessLocationRequestSchema = BusinessLocationSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  businessId: true,
  deletedAt: true,
  createdBy: true,
}).partial();

const BusinessLocationDtoSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  modified_at: z.string(),
  name: z.string(),
  address: z.string(),
  city: z.string(),
  postal_code: z.string(),
  state: z.string(),
  business_id: z.string(),
  deleted_at: z.string().nullable(),
  created_by: z.string().nullable(),
});

export const CreateBusinessLocationDtoSchema = BusinessLocationDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  deleted_at: true,
  created_by: true,
});

export const UpdateBusinessLocationDtoSchema = BusinessLocationDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  business_id: true,
  deleted_at: true,
  created_by: true,
}).partial();

// Infer DTO types
export type BusinessLocationDto = z.infer<typeof BusinessLocationDtoSchema>;
export type CreateBusinessLocationDto = z.infer<
  typeof CreateBusinessLocationDtoSchema
>;
export type UpdateBusinessLocationDto = z.infer<
  typeof UpdateBusinessLocationDtoSchema
>;

// Infer domain types
export type BusinessLocation = z.infer<typeof BusinessLocationSchema>;
export type CreateBusinessLocationRequest = z.infer<
  typeof CreateBusinessLocationRequestSchema
>;
export type UpdateBusinessLocationRequest = z.infer<
  typeof UpdateBusinessLocationRequestSchema
>;

// ============================================================================
// CONVERSION UTILITIES
// ============================================================================

export function fromBusinessLocationDtoToBusinessLocation(
  dto: BusinessLocationDto
): BusinessLocation {
  const locationDto = BusinessLocationDtoSchema.parse(dto);

  return {
    id: locationDto.id,
    name: locationDto.name,
    address: locationDto.address,
    city: locationDto.city,
    state: locationDto.state,
    createdAt: parseISO(locationDto.created_at),
    modifiedAt: locationDto.modified_at
      ? parseISO(locationDto.modified_at)
      : null,
    postalCode: locationDto.postal_code,
    businessId: locationDto.business_id,
    deletedAt: locationDto.deleted_at ? parseISO(locationDto.deleted_at) : null,
    createdBy: locationDto.created_by,
  };
}

export function fromUpdateBusinessLocationRequestToUpdateBusinessLocationDto(
  request: UpdateBusinessLocationRequest
): UpdateBusinessLocationDto {
  const updateBusinessLocationRequest =
    UpdateBusinessLocationRequestSchema.parse(request);

  return UpdateBusinessLocationDtoSchema.parse({
    name: updateBusinessLocationRequest.name,
    address: updateBusinessLocationRequest.address,
    city: updateBusinessLocationRequest.city,
    state: updateBusinessLocationRequest.state,
    postal_code: updateBusinessLocationRequest.postalCode,
  });
}

export function fromCreateBusinessLocationRequestToCreateBusinessLocationDto(
  request: CreateBusinessLocationRequest
): CreateBusinessLocationDto {
  const createBusinessLocationRequest =
    CreateBusinessLocationRequestSchema.parse(request);

  return CreateBusinessLocationDtoSchema.parse({
    name: createBusinessLocationRequest.name,
    address: createBusinessLocationRequest.address,
    city: createBusinessLocationRequest.city,
    postal_code: createBusinessLocationRequest.postalCode,
    state: createBusinessLocationRequest.state,
    business_id: createBusinessLocationRequest.businessId,
  });
}
